import type { NotificationDto, TransportEvent } from './types';
import { NotificationBus } from './transport/NotificationBus';
import { PollingTransport } from './transport/PollingTransport';
import { SharedStatusTransport } from './transport/SharedStatusTransport';
import { Queue } from './Queue';
import { Renderer } from './Renderer';
import { RedirectHandler } from './RedirectHandler';

/**
 * Entry point. Building this with `npm run build` produces public/static/notifications/popup.js,
 * which is included on every non-game page (see views/_inc/notifications_bootstrap.tpl).
 *
 * The PHP page calls window.YcNotificationPopup.start(options) to boot the engine.
 * `options.disabled` skips startup entirely — used on /play/* routes.
 */
export interface BootOptions {
  disabled?: boolean;
  pollingMs?: number;
  /**
   * Delay (ms) before the first poll. The popup engine adds no value during the first few
   * seconds of a page load and we want to stay out of the way of LCP/INP — default 7s.
   */
  initialDelayMs?: number;
  /**
   * URL of the popup stylesheet, lazy-injected on first render. Setting it to an empty
   * string disables lazy injection (assumes the page already loaded the CSS).
   */
  cssUrl?: string;
  endpoints?: {
    pending?: string;
    shown?: string;
    dismissed?: string;
  };
}

const CSS_FLAG = 'data-yc-notification-popup';

/**
 * Inject the popup stylesheet once, on demand. Idempotent — if a <link> with our marker
 * already exists we resolve immediately. Resolves on load so the first popup never appears
 * unstyled (FOUC).
 */
function ensureCss(cssUrl: string): Promise<void> {
  if (!cssUrl) return Promise.resolve();
  const existing = document.querySelector<HTMLLinkElement>(`link[${CSS_FLAG}]`);
  if (existing) {
    return existing.sheet ? Promise.resolve() : new Promise((r) => existing.addEventListener('load', () => r(), { once: true }));
  }
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    link.setAttribute(CSS_FLAG, '1');
    link.addEventListener('load',  () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true }); // never block on CSS error
    document.head.appendChild(link);
  });
}

export function start(opts: BootOptions = {}): void {
  if (opts.disabled) return;

  const endpoints = {
    pending:   opts.endpoints?.pending   ?? '/notifications/pending',
    shown:     opts.endpoints?.shown     ?? '/notifications/shown',
    dismissed: opts.endpoints?.dismissed ?? '/notifications/dismissed',
  };
  const cssUrl = opts.cssUrl ?? '/static/notifications/popup.css';
  const initialDelay = opts.initialDelayMs ?? 7_000;

  const bus       = new NotificationBus();
  const queue     = new Queue();
  const renderer  = new Renderer();
  const redirects = new RedirectHandler(endpoints.shown);

  bus.subscribe('notifications', async (e: TransportEvent) => {
    if (e.type !== 'notification.created' || !e.payload) return;
    const item = e.payload as NotificationDto;

    if (item.action === 'redirect') {
      await redirects.handle(item);
      return;
    }
    queue.enqueue(item);
    void drain();
  });

  async function drain(): Promise<void> {
    let next: NotificationDto | null;
    while ((next = queue.next())) {
      try {
        // Lazy-inject the stylesheet only when we actually have something to show.
        // First render waits for the CSS load; subsequent calls are immediate (idempotent).
        await ensureCss(cssUrl);
        await markShown(next.uuid);
        const result = await renderer.show(next);

        if (result.reason === 'action' && result.actionId) {
          // Required popup — server-side handler decides what to do next.
          const ack = await postAction(next.uuid, result.actionId);
          if (ack && ack.ok && ack.redirect) {
            window.location.assign(ack.redirect);
            return; // halt the drain — the page is about to navigate
          }
          // ack.ok === false → handler refused (e.g. invite expired). We don't re-show here;
          // the next poll will re-deliver if the row is still pending.
        } else {
          await markDismissed(next.uuid);
        }
      } catch (err) {
        console.warn('[NotificationPopup] render failed:', err);
      } finally {
        queue.markDone(next.uuid);
      }
    }
  }

  async function postAction(uuid: string, actionId: string): Promise<{ ok: boolean; redirect?: string; message?: string } | null> {
    try {
      const url = '/notifications/action?uuid=' + encodeURIComponent(uuid) + '&action_id=' + encodeURIComponent(actionId);
      const res = await fetch(url, { method: 'POST', credentials: 'same-origin' });
      if (!res.ok) return null;
      return (await res.json()) as { ok: boolean; redirect?: string; message?: string };
    } catch {
      return null;
    }
  }

  async function markShown(uuid: string): Promise<void> {
    try {
      await fetch(endpoints.shown + '?uuid=' + encodeURIComponent(uuid), {
        method: 'POST',
        credentials: 'same-origin',
      });
    } catch {
      // best-effort; the next poll will re-deliver if needed
    }
  }

  async function markDismissed(uuid: string): Promise<void> {
    try {
      await fetch(endpoints.dismissed + '?uuid=' + encodeURIComponent(uuid), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
      });
    } catch {
      // best-effort
    }
  }

  // Если страница уже держит общий поллер /status, уведомления приходят из него
  // и второй фоновый запрос не нужен. PollingTransport остаётся для страниц без
  // него и как запасной путь: см. docs/perf/POLLING_ENDPOINTS_OPTIMIZATION.md.
  const transport = SharedStatusTransport.isAvailable()
    ? new SharedStatusTransport({ bus })
    : new PollingTransport({
      bus,
      endpoint: endpoints.pending,
      intervalMs: opts.pollingMs ?? 60_000,
    });
  // Defer first poll so the popup engine doesn't compete with critical page-load work.
  // 7s is well past LCP for nearly all pages; subsequent polls run on the normal interval.
  window.setTimeout(() => transport.start(), initialDelay);
}

// IIFE bundle exports — surface a stable global for the PHP page.
export const YcNotificationPopup = { start };
